import {Taillights} from '@github-ui/taillights'
import {useTaillightsController, useTaillightsRendering} from '@github-ui/taillights/provider'
import {taillightsRenderingStyle} from '@github-ui/taillights/rendering-styles'
import type {
  PreparedView,
  SourceRange,
  SourceRect,
  SourceSelection,
  SourceSymbol,
} from '@github-ui/taillights-worker/document'
import {testIdProps} from '@github-ui/test-id-props'
import {ScopedCommands} from '@github-ui/ui-commands'
import {clsx} from 'clsx'
import {
  type FocusEventHandler,
  type KeyboardEventHandler,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import {getNativeSelection, getSourceSelection, type NativeSelectionMap} from './native-selection'
import styles from './taillights-symbols.module.css'
import {useRangeDecorations} from './use-range-decorations'
import {useSymbolSearch} from './use-symbol-search'

export type TaillightsSourceSelection = SourceSelection
export type TaillightsSymbol = SourceSymbol
export type TaillightsSymbolMatch = Omit<SourceSymbol, 'text'>

export type TaillightsSymbolsHandle = {
  inspectSelection: () => void
  focus: () => void
  getSelection: () => SourceSelection | null
  setSelection: (selection: SourceSelection) => boolean
}

// Reaches the source column's padding, where full-row highlights such as a highlighted line start and end.
const rowEdge = 'calc(-1 * var(--source-column-padding, 0px))'

/**
 * Shapes a linked range like the production viewer: one line keeps the range's own box; a range across lines runs
 * from its start to the row's end, fills the rows between, and ends at its end column. Only the start carries the
 * accent; the rows after it are marked in the gutter.
 */
function anchorBlock(rects: readonly SourceRect[], range: SourceRange | null | undefined) {
  const first = rects[0]
  let last = rects.at(-1)
  if (!first || !last || !range) return []
  // An end at a row's first column measures no text; the block still closes on that row, where its text would start.
  if (last.line < range.end.line) {
    const lineStart = rects.find(rect => rect.startColumn === 0)?.x ?? 0
    last = {...last, line: range.end.line, y: last.y + last.height, x: lineStart, width: 0}
  }
  if (first.line === last.line) {
    return rects.map(rect => ({
      key: `${rect.line}-${rect.startColumn}`,
      start: rect === first,
      top: rect.y,
      height: rect.height,
      left: rect.x,
      width: rect.width,
    }))
  }
  const between = last.y - (first.y + first.height)
  return [
    {key: 'start', start: true, top: first.y, height: first.height, left: first.x, right: rowEdge},
    ...(between > 0
      ? [{key: 'between', start: false, top: first.y + first.height, height: between, left: rowEdge, right: rowEdge}]
      : []),
    {
      key: 'end',
      start: false,
      top: last.y,
      height: last.height,
      left: rowEdge,
      width: `calc(${last.x + last.width}px - ${rowEdge})`,
    },
  ]
}

export type SymbolInteractionHandle = TaillightsSymbolsHandle
const NO_FOLDS: readonly number[] = []

export type TaillightsSymbolsProps = {
  'aria-label': string
  view: PreparedView
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  /** A linked range within lines, highlighted with an accent at the start of each row like the production viewer. */
  anchorHighlightRange?: SourceRange | null
  onSelectionChange?: (selection: SourceSelection | null) => void
  onFocus?: FocusEventHandler<HTMLTextAreaElement>
  onBlur?: FocusEventHandler<HTMLTextAreaElement>
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>
  onSymbolSelected?: (symbol: SourceSymbol) => void
  onRenderError?: (error: Error) => void
  ref?: Ref<TaillightsSymbolsHandle>
  textareaRef?: Ref<HTMLTextAreaElement>
  children?: ReactNode
}

export type TaillightsSourceInteractionProps = Omit<TaillightsSymbolsProps, 'view'> & {
  view: PreparedView | null
  /** View the highlight layers follow when the native text lags behind the painted view; defaults to `view`. */
  decorationView?: PreparedView | null
  fallbackText?: string
  hideNativeCaret?: boolean
  initialProjection?: NativeSelectionMap
  hasPlaintextPaint?: boolean
}

/** Keeps native selection and keyboard navigation mounted independently of paint and passive decorations. */
export function TaillightsSourceInteraction({
  'aria-label': ariaLabel,
  view,
  decorationView = view,
  fallbackText,
  hideNativeCaret = false,
  initialProjection,
  hasPlaintextPaint = false,
  focusedHighlightRange,
  anchorHighlightRange,
  highlightRanges,
  onBlur,
  onFocus,
  onKeyDown,
  onSelectionChange,
  onSymbolSelected,
  onRenderError,
  ref,
  textareaRef: externalTextareaRef,
  children,
}: TaillightsSourceInteractionProps) {
  if (!view && !initialProjection && fallbackText === undefined)
    throw new Error('Unprepared source requires fallbackText.')
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const projection = view ?? initialProjection
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const getSelection = useCallback(() => {
    const textarea = textareaRef.current
    return textarea && projection
      ? getSourceSelection(projection, textarea.selectionStart, textarea.selectionEnd, textarea.selectionDirection)
      : null
  }, [projection])
  const {inspectSelection} = useSymbolSearch({getSelection, onSymbolSelected, onRenderError})
  const {controlledRects, focusedRects, anchorRects} = useRangeDecorations({
    collapsed: decorationView?.collapsed ?? NO_FOLDS,
    sourceToDisplay: decorationView?.sourceToDisplay,
    enabled: decorationView !== null,
    highlightRanges,
    focusedHighlightRange,
    anchorHighlightRange,
    onRenderError,
  })

  const attachTextarea = useCallback(
    (node: HTMLTextAreaElement) => {
      textareaRef.current = node
      const cleanup = attachExternalRef(externalTextareaRef, node)
      return () => {
        textareaRef.current = null
        cleanup()
      }
    },
    [externalTextareaRef],
  )

  // Selection the user makes while the painted view is ahead of the textarea is newer than the view's captured
  // selection, so it keeps its place in the source once the textarea takes the new text. Selection this component
  // applied itself is not user input, even when its change is reported later.
  const pendingSelectionRef = useRef<SourceSelection | null>(null)
  const appliedSelectionRef = useRef<{start: number; end: number} | null>(null)
  const applySelection = useCallback(
    (native: {start: number; end: number; direction: 'forward' | 'backward' | 'none'}) => {
      appliedSelectionRef.current = native
      textareaRef.current?.setSelectionRange(native.start, native.end, native.direction)
    },
    [],
  )
  useEffect(() => {
    const pending = pendingSelectionRef.current
    pendingSelectionRef.current = null
    const native = (pending && projection && getNativeSelection(projection, pending)) || view?.nativeSelection
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Native selection must be applied after the projected textarea value commits.
    if (native) applySelection(native)
    // The projection changes with the view; selection is only restored when the view's text arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  useImperativeHandle(
    ref,
    () => ({
      inspectSelection,
      focus: () => textareaRef.current?.focus(),
      getSelection,
      setSelection(selection) {
        const textarea = textareaRef.current
        const native = projection && getNativeSelection(projection, selection)
        if (!textarea || !native) return false
        applySelection(native)
        onSelectionChange?.(getSelection())
        return true
      },
    }),
    [applySelection, getSelection, inspectSelection, onSelectionChange, projection],
  )

  // A controlled value ignores edits without native readOnly disabling caret navigation in Safari.
  return (
    <>
      <div aria-hidden className={styles.BackgroundOverlay}>
        {anchorBlock(anchorRects, anchorHighlightRange).map(({key, start, ...style}) => (
          <span
            key={key}
            className={clsx(styles.AnchorHighlight, !start && styles.AnchorHighlightContinued)}
            {...testIdProps('taillights-anchor-highlight')}
            style={style}
          />
        ))}
        {controlledRects.map(rect => (
          <span
            key={`${rect.line}-${rect.startColumn}-${rect.endColumn}`}
            className={styles.ControlledHighlight}
            {...testIdProps('taillights-controlled-highlight')}
            style={{height: rect.height, left: rect.x, top: rect.y, width: rect.width}}
          />
        ))}
        {focusedRects.map(rect => (
          <span
            key={`${rect.line}-${rect.startColumn}-${rect.endColumn}`}
            className={styles.FocusedHighlight}
            {...testIdProps('taillights-focused-highlight')}
            style={{height: rect.height, left: rect.x, top: rect.y, width: rect.width}}
          />
        ))}
      </div>
      <ScopedCommands
        className={styles.TextareaLayer}
        commands={{'code-view:inspect-symbol': view ? inspectSelection : undefined}}
      >
        <textarea
          ref={attachTextarea}
          aria-label={ariaLabel}
          aria-readonly
          className={clsx(
            styles.Textarea,
            !view && !hasPlaintextPaint && styles.plainText,
            hideNativeCaret && styles.hiddenCaret,
          )}
          {...testIdProps('taillights-symbol-textarea')}
          inputMode="none"
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onMouseUp={event => {
            if (view && event.button === 0 && onSymbolSelected) inspectSelection()
          }}
          onChange={() => {}}
          onSelect={event => {
            const selection = getSelection()
            const applied = appliedSelectionRef.current
            const {selectionStart, selectionEnd} = event.currentTarget
            const userInput = applied?.start !== selectionStart || applied.end !== selectionEnd
            if (decorationView !== view && userInput) pendingSelectionRef.current = selection
            onSelectionChange?.(selection)
          }}
          spellCheck={false}
          style={{tabSize: controller?.profile.tabSize ?? rendering?.tabSize ?? 4}}
          value={projection?.text ?? fallbackText}
        />
      </ScopedCommands>
      {children}
    </>
  )
}

/** Combines retained document paint with one persistent native interaction surface. */
export function TaillightsSymbols({children, ...props}: TaillightsSymbolsProps) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  return (
    <div className={styles.Source} style={taillightsRenderingStyle(controller?.profile ?? rendering)}>
      <div className={styles.SourceContent}>
        <TaillightsSourceInteraction {...props}>
          {/* Keep syntax above native selection without intercepting clicks or disabling child controls. */}
          <div className={styles.PaintLayer} inert aria-hidden {...testIdProps('taillights-paint-layer')}>
            <Taillights view={props.view} inert onRenderError={props.onRenderError} />
          </div>
        </TaillightsSourceInteraction>
      </div>
      {children}
    </div>
  )
}

function attachExternalRef(ref: Ref<HTMLTextAreaElement> | undefined, node: HTMLTextAreaElement) {
  if (typeof ref === 'function') {
    const cleanup = ref(node)
    return () => {
      if (typeof cleanup === 'function') cleanup()
      else ref(null)
    }
  }
  if (ref) ref.current = node
  return () => {
    if (ref) ref.current = null
  }
}
